import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useClassManageStore } from '@/features/class/manage/classManageStore';
import { managedClassToOwnedCard } from '@/features/class/manage/classMappers';
import type {
  ClassCategory,
  ClassCategoryBulkAction,
  ProfileClassOrganizationLayout,
} from '@/features/profile/classes/classCategoryTypes';
import {
  buildProfileClassOrganizationLayout,
  collectAssignedClassIds,
  removeClassIdFromBuckets,
} from '@/features/profile/classes/classOrganizationUtils';
import type { OwnedClassCard } from '@/features/profile/types';

const SEED_CATEGORIES: readonly ClassCategory[] = [
  {
    id: 'cat-stem',
    name: 'STEM Classes',
    classIds: ['class-math101', 'class-math10a'],
    sortOrder: 0,
  },
] as const;

const SEED_UNCATEGORIZED = ['class-math11a', 'class-math10b'] as const;

interface ProfileClassOrganizationState {
  categories: ClassCategory[];
  uncategorizedClassIds: string[];
  isManageCategoriesOpen: boolean;
  pendingBulkAction: { categoryId: string; action: ClassCategoryBulkAction } | null;
  openManageCategories: () => void;
  closeManageCategories: () => void;
  openCategoryBulkAction: (categoryId: string, action: ClassCategoryBulkAction) => void;
  closeCategoryBulkAction: () => void;
  applyOrganization: (payload: {
    categories: readonly ClassCategory[];
    uncategorizedClassIds: readonly string[];
    /**
     * Ids of the classes that should be considered active. When omitted the
     * non-archived classes from the manage store are used. Pass this when the
     * source of truth is the login response (`rawUser.boardWithCategory`) so
     * real backend board ids are not dropped.
     */
    activeClassIds?: readonly string[];
  }) => void;
  confirmCategoryBulkAction: () => void;
  syncWithActiveClasses: (activeClassIds: readonly string[]) => void;
  onClassCreated: (classId: string) => void;
  onClassRemoved: (classId: string) => void;
  getOrganizationLayout: () => ProfileClassOrganizationLayout;
}

function classIdsEqual(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((id, index) => id === right[index]);
}

function categoriesEqual(left: readonly ClassCategory[], right: readonly ClassCategory[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((category, index) => {
    const other = right[index];
    return (
      category.id === other.id &&
      category.name === other.name &&
      category.sortOrder === other.sortOrder &&
      classIdsEqual(category.classIds, other.classIds)
    );
  });
}

function getActiveCardsForLayout(): readonly OwnedClassCard[] {
  return useClassManageStore
    .getState()
    .classes.filter((item) => !item.archived)
    .map(managedClassToOwnedCard);
}

export const useProfileClassOrganizationStore = create<ProfileClassOrganizationState>()(
  persist(
    (set, get) => ({
      categories: [...SEED_CATEGORIES],
      uncategorizedClassIds: [...SEED_UNCATEGORIZED],
      isManageCategoriesOpen: false,
      pendingBulkAction: null,

      openManageCategories: () => {
        set({ isManageCategoriesOpen: true });
      },

      closeManageCategories: () => {
        set({ isManageCategoriesOpen: false });
      },

      openCategoryBulkAction: (categoryId, action) => {
        set({ pendingBulkAction: { categoryId, action } });
      },

      closeCategoryBulkAction: () => {
        set({ pendingBulkAction: null });
      },

      applyOrganization: ({
        categories,
        uncategorizedClassIds: uncategorizedOrder,
        activeClassIds,
      }) => {
        const activeIds =
          activeClassIds ??
          useClassManageStore
            .getState()
            .classes.filter((item) => !item.archived)
            .map((item) => item.id);
        const activeSet = new Set(activeIds);
        const normalized = categories
          .map((category, index) => ({
            ...category,
            name: category.name.trim(),
            classIds: category.classIds.filter((id) => activeSet.has(id)),
            sortOrder: index,
          }))
          .filter((category) => category.name.length > 0);

        const assigned = collectAssignedClassIds(normalized, []);
        const seen = new Set<string>();
        let uncategorizedClassIds = uncategorizedOrder.filter((id) => {
          if (!activeSet.has(id) || assigned.has(id) || seen.has(id)) {
            return false;
          }
          seen.add(id);
          return true;
        });
        for (const classId of activeIds) {
          if (!assigned.has(classId) && !seen.has(classId)) {
            uncategorizedClassIds = [...uncategorizedClassIds, classId];
            seen.add(classId);
          }
        }

        set({
          categories: normalized,
          uncategorizedClassIds,
          isManageCategoriesOpen: false,
        });
      },

      confirmCategoryBulkAction: () => {
        const pending = get().pendingBulkAction;
        if (!pending) {
          return;
        }

        const category = get().categories.find((item) => item.id === pending.categoryId);
        if (!category) {
          set({ pendingBulkAction: null });
          return;
        }

        const classIds = [...category.classIds];
        const manage = useClassManageStore.getState();

        if (pending.action === 'archive') {
          manage.archiveClasses(classIds);
        } else {
          manage.deleteClasses(classIds);
        }

        set((state) => ({
          categories: state.categories.filter((item) => item.id !== pending.categoryId),
          pendingBulkAction: null,
        }));
      },

      syncWithActiveClasses: (activeClassIds) => {
        const activeSet = new Set(activeClassIds);
        set((state) => {
          const categories = state.categories.map((category) => ({
            ...category,
            classIds: category.classIds.filter((id) => activeSet.has(id)),
          }));

          let uncategorizedClassIds = state.uncategorizedClassIds.filter((id) => activeSet.has(id));

          const assigned = collectAssignedClassIds(categories, uncategorizedClassIds);
          for (const classId of activeClassIds) {
            if (!assigned.has(classId)) {
              uncategorizedClassIds = [...uncategorizedClassIds, classId];
            }
          }

          if (
            categoriesEqual(categories, state.categories) &&
            classIdsEqual(uncategorizedClassIds, state.uncategorizedClassIds)
          ) {
            return state;
          }

          return { categories, uncategorizedClassIds };
        });
      },

      onClassCreated: (classId) => {
        set((state) => {
          if (
            state.uncategorizedClassIds.includes(classId) ||
            state.categories.some((category) => category.classIds.includes(classId))
          ) {
            return state;
          }
          return {
            uncategorizedClassIds: [...state.uncategorizedClassIds, classId],
          };
        });
      },

      onClassRemoved: (classId) => {
        set((state) =>
          removeClassIdFromBuckets(state.categories, state.uncategorizedClassIds, classId),
        );
      },

      getOrganizationLayout: () => {
        const state = get();
        return buildProfileClassOrganizationLayout(
          getActiveCardsForLayout(),
          state.categories,
          state.uncategorizedClassIds,
        );
      },
    }),
    {
      name: 'myu-profile-class-organization',
      partialize: (state) => ({
        categories: state.categories,
        uncategorizedClassIds: state.uncategorizedClassIds,
      }),
    },
  ),
);

/** Keeps organization buckets aligned with active (non-archived) classes. */
export function syncProfileClassOrganization(): void {
  const activeIds = useClassManageStore
    .getState()
    .classes.filter((item) => !item.archived)
    .map((item) => item.id);
  useProfileClassOrganizationStore.getState().syncWithActiveClasses(activeIds);
}
